import { Icon } from '@99designs/common/components/Icon';
import { SquareOnlineStoreBriefFields } from '__generated__/SquareOnlineStoreBriefFields';
import { StructuredBriefFile } from '__generated__/StructuredBriefFile';
import { SquareOnlineStoreExistingAssets, SquareOnlineStoreProjectType } from '__generated__/types';
import { Matrix, MatrixItem } from 'common/components/Matrix';
import Mediabox from 'common/components/Mediabox';
import { Currency, toUnit } from 'common/utils/currency';
import { formatAndLinkify } from 'projects/components/FormatAndLinkify';
import { linkify } from 'projects/components/Linkify';
import SummaryCard from 'projects/components/summary/SummaryCard';
import SummaryCardItem from 'projects/components/summary/SummaryCardItem';
import { TimeframeSummary } from 'projects/components/TimeFrame';
import { trackEditBriefEvent, trackViewedBriefEvent } from 'projects/events';
import { routes } from 'projects/routes';
import { Industries } from 'projects/util/industries';
import React, { useEffect } from 'react';
import { FormattedNumber } from 'react-intl';

interface Props {
    brief: {
        id: string;
        fields: SquareOnlineStoreBriefFields;
        designers: { displayName: string }[];
    };
    industries: Industries;
}

const projectType = (projectType: SquareOnlineStoreProjectType) => {
    switch (projectType) {
        case SquareOnlineStoreProjectType.NEW_SITE:
            return 'Create a new Square Online Store';
        case SquareOnlineStoreProjectType.RECREATE_SITE:
            return 'Migrate an existing site to Square Online Store';
        case SquareOnlineStoreProjectType.MINOR_CHANGES:
            return 'Make minor changes to a Square Online Store';
        case SquareOnlineStoreProjectType.MAJOR_CHANGES:
            return 'Redesign or make major changes to a Square Online Store';
        default:
            return projectType;
    }
};

const existingAssets = (existingAssets: SquareOnlineStoreExistingAssets[]) => {
    return existingAssets.map((asset, id) => {
        switch (asset) {
            case SquareOnlineStoreExistingAssets.LOGO_AND_BRANDING:
                return <div key={id}> {__('Logo & Branding')} </div>;
            case SquareOnlineStoreExistingAssets.IMAGES_AND_VISUAL_ASSETS:
                return <div key={id}> {__('Images & Visual Assets')} </div>;
            case SquareOnlineStoreExistingAssets.WEBSITE_AND_PRODUCT_COPY:
                return <div key={id}> {__('Website & Product Copy')} </div>;
        }
    });
};

const currency = Currency.USD;

const budget = (budget: number) => {
    return budget === 0 ? (
        __('Any price')
    ) : (
        <FormattedNumber
            value={toUnit(budget, currency)}
            style="currency"
            currency={currency}
            minimumFractionDigits={0}
        />
    );
};

const SummaryPage: (props: Props) => React.ReactElement<Props> = ({ brief, industries }) => {
    useEffect(() => {
        trackViewedBriefEvent({
            id: brief.id,
            page: 'Summary',
        });
    }, []);

    return (
        <div className="content-setting">
            <SummaryCard
                heading={__('Tell us about your project')}
                onEditClick={() =>
                    trackEditBriefEvent({
                        id: brief.id,
                        page: 'Details',
                    })
                }
                url={routes.brief(brief, 1)}
            >
                <SummaryCardItem title={__('Name your project')}>
                    {brief.fields.title}
                </SummaryCardItem>
                <SummaryCardItem title={__('What best describes your need?')}>
                    {projectType(brief.fields.projectType)}
                </SummaryCardItem>
                <SummaryCardItem title={__('Describe your project')}>
                    {formatAndLinkify(brief.fields.description)}
                </SummaryCardItem>
                {brief.fields.referenceFiles.length > 0 && (
                    <SummaryCardItem title={__('Add reference files')}>
                        <Matrix large={3}>
                            {brief.fields.referenceFiles.map(
                                (attachment: StructuredBriefFile, id: number) => {
                                    return (
                                        <MatrixItem key={id}>
                                            <Mediabox>
                                                <div className="uploader-attachment-overlay">
                                                    <div className="uploader-attachment-overlay__inner">
                                                        <div className="uploader-attachment-overlay__content">
                                                            {attachment.previewable ? (
                                                                <img
                                                                    src={`https://${
                                                                        attachment.bucket
                                                                    }.s3-accelerate.amazonaws.com/${
                                                                        attachment.key
                                                                    }`}
                                                                />
                                                            ) : (
                                                                <Icon
                                                                    type={'document-1'}
                                                                    variations={['2x']}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Mediabox>
                                        </MatrixItem>
                                    );
                                }
                            )}
                        </Matrix>
                    </SummaryCardItem>
                )}
                {brief.fields.inspirationFiles.length > 0 && (
                    <SummaryCardItem title={__('Add inspiration files')}>
                        <Matrix large={3}>
                            {brief.fields.inspirationFiles.map(
                                (attachment: StructuredBriefFile, id: number) => {
                                    return (
                                        <MatrixItem key={id}>
                                            <Mediabox>
                                                <div className="uploader-attachment-overlay">
                                                    <div className="uploader-attachment-overlay__inner">
                                                        <div className="uploader-attachment-overlay__content">
                                                            {attachment.previewable ? (
                                                                <img
                                                                    src={`https://${
                                                                        attachment.bucket
                                                                    }.s3-accelerate.amazonaws.com/${
                                                                        attachment.key
                                                                    }`}
                                                                />
                                                            ) : (
                                                                <Icon
                                                                    type={'document-1'}
                                                                    variations={['2x']}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Mediabox>
                                        </MatrixItem>
                                    );
                                }
                            )}
                        </Matrix>
                    </SummaryCardItem>
                )}
                {brief.fields.inspirationUrls.length > 0 && (
                    <SummaryCardItem title={__('Link to any inspiration on the Web')}>
                        {linkify(brief.fields.inspirationUrls)}
                    </SummaryCardItem>
                )}
                {brief.fields.existingAssets.length > 0 && (
                    <SummaryCardItem title={__('Do you have any of the following?')}>
                        {existingAssets(brief.fields.existingAssets)}
                    </SummaryCardItem>
                )}
            </SummaryCard>
            <SummaryCard
                heading={__('Your timeline')}
                onEditClick={() =>
                    trackEditBriefEvent({
                        id: brief.id,
                        page: 'TimeAndBudget',
                    })
                }
                url={routes.brief(brief, 3)}
            >
                <SummaryCardItem title={__("What's your timeline?")}>
                    <TimeframeSummary
                        value={brief.fields.timeframe}
                        startDate={brief.fields.startDate}
                        endDate={brief.fields.endDate}
                    />
                </SummaryCardItem>
                <SummaryCardItem title={__("What's your budget?")}>
                    {budget(brief.fields.budget)}
                </SummaryCardItem>
            </SummaryCard>
            <SummaryCard
                heading={__('About you')}
                onEditClick={() =>
                    trackEditBriefEvent({
                        id: brief.id,
                        page: 'AboutYou',
                    })
                }
                url={routes.brief(brief, 2)}
            >
                {industries.find(brief.fields.industry) && (
                    <SummaryCardItem title={__('What industry are you in?')}>
                        {industries.find(brief.fields.industry)}
                    </SummaryCardItem>
                )}
                {brief.fields.urls.length > 0 && (
                    <SummaryCardItem title={__('Link to your existing website or store')}>
                        {linkify(brief.fields.urls)}
                    </SummaryCardItem>
                )}
                <SummaryCardItem
                    title={__(
                        'Can your designer showcase the finished designs in their 99designs portfolio?'
                    )}
                >
                    {brief.fields.profileApproval ? __('Yes') : __('No')}
                </SummaryCardItem>
                {brief.fields.other != '' && (
                    <SummaryCardItem
                        title={__("Anything else you'd like to share with your designer?")}
                    >
                        {formatAndLinkify(brief.fields.other)}
                    </SummaryCardItem>
                )}
            </SummaryCard>
        </div>
    );
};

export default SummaryPage;
